import {parseInteger} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import FieldActionButtons from 'metadata/FieldActionButtons';
import {MethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import {validateDilutionFactor} from 'procedures/design/validation/private-validation-helpers/validateDilutionPlan';
import {Method} from 'procedures/ProcedureDomainTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import {GRAY_SEMIDARK, NEAR_BLACK} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import TextValue from 'ui-library/TextValue';
import {immutableInsert, immutableMove, immutableReplace} from 'util/ImmutableListUtil';
import * as keyboard from 'util/Keyboard';
import removeNulls from 'util/removeNulls';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

interface DilutionFactorFieldProps {
  value: number | null;
  isReadOnly: boolean;
  isDefault: boolean;
  handleUpdate: (newDilutionFactor: number | null) => void;
  handleEnter: () => void;
  handleDelete: () => void;
  isDuplicateDilutionFactor: (dilutionFactor: number) => boolean;
}

/** Single dilution factor field component. Handles validation and rendering either in view only or
 * in editing mode.
 * Exported for testing.
 */
export function DilutionFactorField(props: DilutionFactorFieldProps): JSX.Element {
  const initialDilutionFactor = props.value?.toString() ?? '';
  const placeholder = 'Enter dilution factor';
  const defaultDilutionText = ' (default)';

  const [editingValue, setEditingValue] = useState<string>(initialDilutionFactor);

  function getValidationErrorMessage(value: string | null): string | undefined {
    if (value === '' || value == null) {
      // don't show error on an empty value
      return undefined;
    } else {
      const numericDilutionFactor = parseInteger(value);
      const invalidDilutionError = validateDilutionFactor(numericDilutionFactor);
      if (invalidDilutionError != null) {
        return invalidDilutionError;
      } else if (props.isDuplicateDilutionFactor(numericDilutionFactor!)) {
        return 'Dilution factor already exists';
      }
    }
    return undefined;
  }

  const errorMessage = getValidationErrorMessage(editingValue);

  function handleChangeValue(e: InputChangeEvent): void {
    setEditingValue(e.target.value);
    props.handleUpdate(e.target.value !== '' ? Number(e.target.value) : null);
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      if (!errorMessage) {
        props.handleEnter();
      }
    }
  }

  if (props.isReadOnly) {
    return (
      <div className={css(styles.fieldWrapper)}>
        <div className={css(styles.editableField)}>
          <TextValue
            value={`${props.value}${props.isDefault ? defaultDilutionText : ''}`}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={css(styles.fieldWrapper)}>
      <div className={css(styles.editableField)}>
        <FormInput
          disabled={false}
          onChange={handleChangeValue}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          focusOnMount={true}
          value={editingValue}
          selectAllOnClick={true}
          type="number"
          error={errorMessage}
        />
        <FieldActionButtons isEditing={false} remove={props.handleDelete} />
      </div>
      {errorMessage && <FieldError message={errorMessage} compact={true} />}
    </div>
  );
}

export interface DilutionsTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  locked: boolean; // from tests tab
}

/**
 * This type is only used locally in this component. It allows us to keep track
 * of the user's choices while they make edits, without being limited to the data
 * type used by the database. Specifically, we track values by UUID, which is not
 * available on the domain model.
 */
interface EditableDilutionFactor {
  /** null means the factor is empty */
  value: number | null;
  /** The uuid is only used in this component, and is stable while the component instance exists */
  uuid: string;
}

/**
 * DilutionsTable is a React component that renders a table for managing dilution factors.
 * It allows users to add, edit, delete, and reorder dilution factors.
 *
 * Props:
 * - `method`: The method object containing the dilution plan.
 * - `onChangeMethod`: Callback function to handle changes to the method.
 * - `locked`: Boolean indicating if the table is locked for editing.
 */
export default function DilutionsTable(props: DilutionsTableProps): JSX.Element {
  const {locked} = props;

  // We keep track of dilution factors locally, separate from what we get on props
  const [localFactors, setLocalFactors] = useState<Array<EditableDilutionFactor>>(
    props.method.dilutionPlan.allowedDilutionFactors.map((factor) => ({
      value: factor,
      uuid: uuidv4(),
    }))
  );

  // Track the last values we sent to the parent to distinguish external changes (like cancel)
  // from our own internal changes
  const lastSentFactorsRef = useRef<Array<number>>(removeNulls(localFactors.map(({value}) => value)));

  // This effect is the only time we alert the parent component about changes
  useEffect(() => {
    const nonEmptyDilutionFactors = removeNulls(localFactors.map(({value}) => value));
    lastSentFactorsRef.current = nonEmptyDilutionFactors;
    props.onChangeMethod(props.method.cid, (m) => ({
      ...m,
      dilutionPlan: {
        ...m.dilutionPlan,
        allowedDilutionFactors: nonEmptyDilutionFactors,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFactors]);

  // Sync localFactors from props when props change externally (e.g., on cancel)
  // We only sync when the prop values differ from what we last sent to avoid loops
  useEffect(() => {
    const propFactors = props.method.dilutionPlan.allowedDilutionFactors;
    const lastSentFactors = lastSentFactorsRef.current;

    // Only sync if props differ from what we last sent (indicating external change like cancel)
    // This prevents syncing when our own changes flow back through props
    if (!isEqual(propFactors, lastSentFactors)) {
      setLocalFactors(
        propFactors.map((factor) => ({
          value: factor,
          uuid: uuidv4(),
        }))
      );
      // Store a copy to avoid reference issues
      lastSentFactorsRef.current = [...propFactors];
    }
  }, [props.method.dilutionPlan.allowedDilutionFactors, setLocalFactors]);

  useEffect(() => {
    if (locked) {
      // When locking, remove all null values from our local list. This ensures that we show the "no dilutions" message,
      // and generally ensures that our local dilutions list matches the parent's
      const factorsWithNullsRemoved = localFactors.filter(({value}) => value != null);
      if (factorsWithNullsRemoved.length !== localFactors.length) {
        setLocalFactors(factorsWithNullsRemoved);
      }
    } else if (localFactors.length === 0) {
      // Add a single empty dilution factor if none are present
      onAddNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  function onAddNew(atIndex: number | null = null): void {
    const newItem: EditableDilutionFactor = {value: null, uuid: uuidv4()};
    setLocalFactors(immutableInsert(localFactors, atIndex ?? localFactors.length, newItem));
  }

  /**
   * Reordering is blocked if:
   *  - There aren't enough dilution factors to reorder (i.e., < 2), or
   *  - There are any empty dilution factors, or
   *  - There are any duplicates
   */
  function canReorder(): boolean {
    return (
      !locked &&
      localFactors.length >= 2 &&
      localFactors.every((factor) => factor.value != null) &&
      new Set(localFactors.map(({value}) => value)).size === localFactors.length
    );
  }

  function isDuplicateDilutionFactorForRow(dilutionFactor: number, rowIdx: number): boolean {
    return Boolean(localFactors.find((existing, idx) => idx !== rowIdx && existing.value === dilutionFactor));
  }

  function updateDilutionFactor(rowIdx: number, value: number | null): void {
    const factorToUpdate = localFactors[rowIdx];
    setLocalFactors(immutableReplace(localFactors, rowIdx, {...factorToUpdate, value}));
  }

  function getRows(): Array<Row> {
    return localFactors.map((dilutionFactor, rowIdx) => ({
      id: dilutionFactor.uuid,
      cells: [
        {
          key: 'value',
          contents: (
            <DilutionFactorField
              value={dilutionFactor.value}
              isReadOnly={locked}
              // The first dilution factor is the default
              isDefault={rowIdx === 0 && localFactors.length > 0}
              handleUpdate={(newDilutionFactor: number | null) => {
                updateDilutionFactor(rowIdx, newDilutionFactor);
              }}
              handleEnter={() => {
                onAddNew(rowIdx + 1);
              }}
              handleDelete={() => {
                onDelete(rowIdx);
              }}
              isDuplicateDilutionFactor={(dilutionFactor) =>
                isDuplicateDilutionFactorForRow(dilutionFactor, rowIdx)
              }
            />
          ),
        },
      ],
    }));
  }

  function onDelete(rowIdx: number): void {
    setLocalFactors(localFactors.filter((_, idx) => idx !== rowIdx));
  }

  function onReorderRow(rowId: string, newIndex: number): void {
    const factorToMove = localFactors.find((factor) => factor.uuid === rowId);
    assert(factorToMove != null, 'Unable to find dilution factor for reorder', {
      localFactors,
      rowId,
    });
    setLocalFactors(immutableMove(localFactors, factorToMove, newIndex));
  }

  return (
    <div
      className={css(styles.dilutionTableWrapper)}
      {...getTestProps({component: 'DilutionsTable', element: 'root'})}
    >
      <ReorderableSimpleTable
        headerCells={[
          {
            key: 'dilutions',
            contents: (
              <div className={css(styles.dilutionTableHeader)}>
                <div className={css(styles.dilutionTableTitle)}>
                  Dilution factors
                  {!locked && (
                    <ClickableIcon
                      icon="plus"
                      iconStyle={ClickableIconStyle.POSITIVE}
                      onClick={() => {
                        onAddNew();
                      }}
                      disabled={locked}
                      dataTestAttrs={{component: 'DilutionsTable', element: 'addRowButton'}}
                    />
                  )}
                </div>
                Specify acceptable dilution factors for testing samples. Tests will be ordered when samples
                are ingested, according to the dilution assignment.
              </div>
            ),
          },
        ]}
        isDraggingDisabled={!canReorder()}
        onReorderRow={onReorderRow}
        noRowsDisplay="No dilutions added"
        rows={getRows()}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  dilutionTableWrapper: {
    width: 376,
  },
  dilutionTableHeader: {
    fontWeight: 400,
    fontSize: 12,
    color: GRAY_SEMIDARK,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  dilutionTableTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: NEAR_BLACK,
    display: 'flex',
    gap: 4,
  },
  editableField: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  fieldWrapper: {
    marginRight: '-4px',
  },
});
